import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { Base64File, FileUpload, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import {
  DocumentUploadRejectedError,
  getDataCollectionErrorCode,
  isTerminalTransactionError,
} from '@methods/dataCollection'
import type { DocumentRequest, UploadedDocument } from '@/types/dataCollection'
import {
  formatContentTypes,
  resolveLabel,
  slotMaxFiles,
  slotMaxSizeMb,
  slotMinFiles,
} from './helpers'

interface UploadSlotProps {
  request: DocumentRequest
  documents: UploadedDocument[]
  requiredError?: string
  onUpload: (request: DocumentRequest, file: Base64File) => Promise<void>
  onDelete: (documentId: string) => Promise<void>
  onUploadingChange?: (uploading: boolean) => void
}

export const UploadSlot = observer(
  ({
    request,
    documents,
    requiredError,
    onUpload,
    onDelete,
    onUploadingChange,
  }: UploadSlotProps) => {
    const store = useContext(StoreContext)
    const { language } = store.TranslationsState
    const { dataCollectionPage: trans } = store.TranslationsState.translations

    const inputId = `base64FileInput-${request.id}`
    const maxFiles = slotMaxFiles(request)
    const maxSizeMb = slotMaxSizeMb(request)
    const canAddMore = documents.length < maxFiles

    const [uploading, setUploading] = useState(false)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const [error, setError] = useState('')
    const [fileInputKey, setFileInputKey] = useState(0)

    const queueRef = useRef<Base64File[]>([])
    const processingRef = useRef(false)

    const resetInput = () => setFileInputKey((k) => k + 1)

    const mapUploadError = (err: unknown): string => {
      // S3 rejected the direct upload: policy violated or the 5-min TTL expired.
      // The S3 body carries no friendly reason, so show the generic per-slot hint.
      if (err instanceof DocumentUploadRejectedError) {
        return trans.upload.uploadRejected ?? trans.upload.failed
      }
      switch (getDataCollectionErrorCode(err)) {
        case 'DataCollection.UnsupportedFileType':
          return trans.upload.unsupportedType
        case 'DataCollection.FileTooLarge':
          return trans.upload.fileTooLarge.replace('{maxSizeMb}', String(maxSizeMb))
        case 'DataCollection.InvalidPdfFile':
          return trans.upload.invalidPdf
        case 'DataCollection.InvalidImageFile':
          return trans.upload.invalidImage ?? trans.upload.unsupportedType
        case 'DataCollection.ImageTooManyPixels':
          return (
            trans.upload.imageTooLarge ??
            trans.upload.fileTooLarge.replace('{maxSizeMb}', String(maxSizeMb))
          )
        case 'DataCollection.TooManyDocuments':
          return trans.upload.tooManyFiles
        case 'DataCollection.UploadLimitReached':
          return (
            trans.upload.uploadLimitReached ??
            'Upload limit reached. Please contact support or start over.'
          )
        case 'DataCollection.DocumentNotUploaded':
          return trans.upload.uploadRejected ?? trans.upload.failed
        default:
          return trans.upload.failed
      }
    }

    const processQueue = async () => {
      if (processingRef.current) return
      processingRef.current = true
      setUploading(true)
      onUploadingChange?.(true)
      setError('')

      let count = documents.length
      try {
        while (queueRef.current.length > 0) {
          const file = queueRef.current.shift() as Base64File
          if (count >= maxFiles) {
            setError(trans.upload.tooManyFiles)
            continue
          }
          if (!request.allowedContentTypes.includes(file.type)) {
            setError(trans.upload.unsupportedType)
            continue
          }
          if (file.sizeMB > maxSizeMb) {
            setError(trans.upload.fileTooLarge.replace('{maxSizeMb}', String(maxSizeMb)))
            continue
          }
          try {
            await onUpload(request, file)
            count++
          } catch (err) {
            // Frozen or completed transaction: the page is navigating away. Drop the rest of
            // the queue quietly rather than reporting failures for a flow that is over.
            if (isTerminalTransactionError(err)) {
              queueRef.current = []
              break
            }
            setError(mapUploadError(err))
            // The upload budget is a hard per-transaction cap: retrying can't
            // succeed, so drop any remaining queued files instead of firing
            // more doomed requests.
            if (getDataCollectionErrorCode(err) === 'DataCollection.UploadLimitReached') {
              queueRef.current = []
            }
          }
        }
      } finally {
        processingRef.current = false
        setUploading(false)
        onUploadingChange?.(false)
        resetInput()
      }
    }

    const handleFile = (file: Base64File) => {
      queueRef.current.push(file)
      void processQueue()
    }

    const handleDelete = async (documentId: string) => {
      if (deletingId || uploading) return

      setError('')
      setDeletingId(documentId)
      onUploadingChange?.(true)
      try {
        await onDelete(documentId)
      } catch (err) {
        if (!isTerminalTransactionError(err)) setError(trans.upload.failed)
      } finally {
        setDeletingId(null)
        onUploadingChange?.(false)
      }
    }

    const hasUploads = documents.length > 0
    const shownError = error || requiredError

    const minFiles = slotMinFiles(request)
    const missingFiles = Math.max(0, minFiles - documents.length)
    const minFilesHint =
      missingFiles > 0 && !shownError
        ? (trans.upload.minFilesHint ?? 'Required files uploaded: {uploaded} of {minFiles}')
            .replace('{uploaded}', String(documents.length))
            .replace('{minFiles}', String(minFiles))
            .replace('{missing}', String(missingFiles))
        : ''
    const removeLabel = trans.upload.remove ?? 'Remove file'
    const dropHereLabel = trans.upload.dropHere ?? 'Drop here'
    const dropText = trans.upload.dropText ?? 'Drag here or'
    const dropAction = trans.upload.dropAction ?? 'select'
    const formatHint = (trans.upload.formatHint ?? '{formats} : max. {maxSizeMb}MB')
      .replace('{formats}', formatContentTypes(request.allowedContentTypes))
      .replace('{maxSizeMb}', String(maxSizeMb))

    return (
      <div className="data-collection__slot">
        <span className="data-collection__label">{resolveLabel(request.label, language)}</span>
        {request.description && (
          <p className="data-collection__slot-description">
            {resolveLabel(request.description, language)}
          </p>
        )}

        {hasUploads && (
          <ul className="data-collection__uploaded-list">
            {documents.map((doc) => (
              <li className="data-collection__uploaded-item" key={doc.documentId}>
                <span className="data-collection__uploaded-name">{doc.fileName}</span>
                <button
                  type="button"
                  className="data-collection__uploaded-remove"
                  aria-label={removeLabel}
                  disabled={deletingId !== null || uploading}
                  onClick={() => handleDelete(doc.documentId)}
                >
                  <Icon type="trash" size="20px" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {canAddMore && (
          <FileUpload
            inputId={inputId}
            inputKey={fileInputKey}
            onSelect={handleFile}
            multiple={maxFiles > 1}
            disabled={uploading}
            accept={request.allowedContentTypes.join(',')}
            allowedTypes={request.allowedContentTypes}
            invalidTypeMessage={trans.upload.unsupportedType}
            onTypeError={(msg) => setError(msg)}
            draggingLabel={dropHereLabel}
            icon={
              <span className="data-collection__dropzone-icon">
                <Icon
                  type="upload"
                  size="20px"
                  color="var(--data-collection-icon-color, #76be74)"
                />
              </span>
            }
            title={
              <span className="data-collection__dropzone-text">
                <span className="data-collection__dropzone-hint">
                  {dropText} <span className="data-collection__dropzone-action">{dropAction}</span>
                </span>
                <span className="data-collection__dropzone-formats">{formatHint}</span>
              </span>
            }
            uploadButton={
              <span className="data-collection__dropzone-button">
                {uploading ? (
                  <LoadingSpinner inButton />
                ) : hasUploads ? (
                  trans.upload.addAnother
                ) : (
                  trans.upload.button
                )}
              </span>
            }
          />
        )}

        {shownError ? (
          <span className="data-collection__error">{shownError}</span>
        ) : minFilesHint ? (
          <span className="data-collection__slot-hint">{minFilesHint}</span>
        ) : null}
      </div>
    )
  }
)
